import { useState, useCallback } from 'react';
import type { ProjectDetail } from '../../services/projectService';
import { useBacklogIssues, useSprints, useAddItemToSprint, useBulkAddItems } from '../../hooks/useSprints';
import BacklogPanel from './BacklogPanel';
import SprintPanel from './SprintPanel';
import BulkAssignBar from './BulkAssignBar';

interface Props {
  project: ProjectDetail;
}

export interface BacklogFilter {
  issueType: string[];
  priority: string[];
  assigneeId: string;
}

const EMPTY_FILTER: BacklogFilter = {
  issueType: [],
  priority: [],
  assigneeId: '',
};

export default function BacklogView({ project }: Props) {
  const [filter, setFilter] = useState<BacklogFilter>(EMPTY_FILTER);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const { data: backlogIssues = [], isLoading: backlogLoading } = useBacklogIssues(
    project.id,
    {
      issueType: filter.issueType.length ? filter.issueType : undefined,
      priority: filter.priority.length ? filter.priority : undefined,
      assigneeId: filter.assigneeId || undefined,
    },
  );
  const { data: sprints = [], isLoading: sprintsLoading } = useSprints(project.id);

  const addItemMutation = useAddItemToSprint(project.id);
  const bulkAddMutation = useBulkAddItems(project.id);

  const handleDropToSprint = useCallback(
    (sprintId: string, issueId: string) => {
      addItemMutation.mutate({ sprintId, issueId });
    },
    [addItemMutation],
  );

  const handleBulkAssign = useCallback(
    (sprintId: string) => {
      const issueIds = Array.from(selectedIds);
      if (issueIds.length === 0) return;
      bulkAddMutation.mutate(
        { sprintId, issueIds },
        { onSuccess: () => setSelectedIds(new Set()) },
      );
    },
    [selectedIds, bulkAddMutation],
  );

  const toggleSelect = useCallback((issueId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(issueId)) next.delete(issueId);
      else next.add(issueId);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      if (prev.size === backlogIssues.length) return new Set();
      return new Set(backlogIssues.map((i) => i.id));
    });
  }, [backlogIssues]);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  return (
    <div className="flex h-full gap-4 relative">
      {/* Left: Backlog Panel */}
      <div className="w-[360px] shrink-0 flex flex-col min-h-0">
        <BacklogPanel
          issues={backlogIssues}
          isLoading={backlogLoading}
          filter={filter}
          onFilterChange={setFilter}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
          onToggleSelectAll={toggleSelectAll}
          members={project.members}
        />
      </div>

      {/* Right: Sprint Panel */}
      <div className="flex-1 min-w-0 min-h-0">
        <SprintPanel
          project={project}
          sprints={sprints}
          isLoading={sprintsLoading}
          onDropIssue={handleDropToSprint}
        />
      </div>

      {/* Bottom: Bulk Assign Bar */}
      {selectedIds.size > 0 && (
        <BulkAssignBar
          count={selectedIds.size}
          sprints={sprints}
          onAssign={handleBulkAssign}
          onClear={clearSelection}
          isLoading={bulkAddMutation.isPending}
        />
      )}
    </div>
  );
}
